import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Bell, Moon, Layers, Sun, Loader2, Save, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import ToggleSwitch from '../components/ToggleSwitch';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import { normalizeRole } from '../utils/roleUtils';
import { formatRecordDate, formatRecordTime } from '../utils/dateUtils';

// Bildirim türleri konuya göre gruplanır; anahtar adları sunucudaki ayar şemasıyla birebir aynıdır.
const EVENT_GROUPS = [
    {
        title: 'Tahsilat',
        items: [
            { key: 'collectionCollected', label: 'Tahsil edildi', hint: 'Bir kayıt ilk kez "tahsil edildi" işaretlendiğinde' },
        ],
    },
    {
        title: 'Sevkiyat',
        items: [{ key: 'shipmentCreated', label: 'Yeni sevkiyat kaydı', hint: 'Web veya mobilden sevkiyat eklendiğinde (fotoğrafıyla)' }],
    },
    {
        title: 'İdari takip',
        items: [
            { key: 'taskDue', label: 'Görev bugün son', hint: 'Son tarihi bugün olan ve tamamlanmamış görev için gün başına bir kez' },
            { key: 'taskCompleted', label: 'Görev tamamlandı', hint: 'Görev tamamlandı / ödendi olarak işaretlendiğinde' },
        ],
    },
    {
        title: 'Mutabakat',
        items: [{ key: 'reconciliationDone', label: 'Mutabakat yapıldı', hint: '"Mutabakat Yap" ile yeni kayıt girildiğinde' }],
    },
];

const SUMMARY_SECTIONS = [
    { key: 'collections', label: 'Bekleyen tahsilatlar', hint: '10+ gündür bekleyenler, en eski kayıtlar' },
    { key: 'tasks', label: 'İdari görevler', hint: 'Gecikmiş ve yarın son olan görevler' },
    { key: 'reconciliation', label: 'Mutabakat', hint: 'Süresi geçen, yaklaşan ve hiç yapılmamış firmalar' },
    { key: 'shipments', label: 'Sevkiyat', hint: 'Onay bekleyenler (onay süreci açıksa) ve 2+ gündür sevk edilmeyenler' },
];

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const inputClass = 'rounded-lg border border-dark-border bg-dark-bg px-3 py-2 text-sm text-dark-text focus:border-primary-500 focus:outline-none disabled:opacity-50';

const SectionCard = ({ icon, title, description, children }) => (
    <section className="rounded-2xl border border-dark-border bg-dark-card p-5">
        <div className="mb-4 flex items-start gap-3">
            <span className="mt-0.5 text-primary-500">{icon}</span>
            <div>
                <h2 className="text-base font-bold text-dark-text">{title}</h2>
                {description && <p className="mt-0.5 text-xs text-dark-muted">{description}</p>}
            </div>
        </div>
        {children}
    </section>
);

const Row = ({ label, hint, children }) => (
    <div className="flex items-center justify-between gap-4 border-t border-dark-border py-3 first:border-t-0 first:pt-0 last:pb-0">
        <div className="min-w-0">
            <p className="text-sm font-medium text-dark-text">{label}</p>
            {hint && <p className="text-xs text-dark-muted">{hint}</p>}
        </div>
        <div className="shrink-0">{children}</div>
    </div>
);

const NotificationSettings = () => {
    const { user } = useContext(AuthContext);
    const isAdmin = normalizeRole(user?.role) === 'admin';

    const [settings, setSettings] = useState(null);
    const [savedSettings, setSavedSettings] = useState(null);
    const [meta, setMeta] = useState({ updatedAt: null, updatedBy: null, isDefault: true });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const applyResponse = useCallback((data) => {
        setSettings(data.settings);
        setSavedSettings(data.settings);
        setMeta({ updatedAt: data.updatedAt, updatedBy: data.updatedBy, isDefault: data.isDefault });
    }, []);

    useEffect(() => {
        if (!isAdmin) return;

        api.get('/notifications/settings')
            .then((res) => applyResponse(res.data))
            .catch(() => toast.error('Bildirim ayarları yüklenemedi.'))
            .finally(() => setIsLoading(false));
    }, [isAdmin, applyResponse]);

    const isDirty = useMemo(() => Boolean(settings) && !sameJson(settings, savedSettings), [settings, savedSettings]);

    // Sunucu aynı kuralları doğrular; burada kaydetmeden önce kullanıcıya anlaşılır uyarı gösterilir
    const validationError = useMemo(() => {
        if (!settings) return null;
        if (settings.quietHours.start === settings.quietHours.end) return 'Sessiz saat başlangıcı ve bitişi aynı olamaz.';
        const threshold = Number(settings.burstThreshold);
        if (!Number.isInteger(threshold) || threshold < 1 || threshold > 20) return 'Toplu mesaj eşiği 1 ile 20 arasında bir sayı olmalıdır.';
        if (!settings.quietHours.start || !settings.quietHours.end || !settings.morningSummary.time) return 'Saat alanları boş bırakılamaz.';
        return null;
    }, [settings]);

    const update = (mutator) => setSettings((current) => {
        const next = JSON.parse(JSON.stringify(current));
        mutator(next);
        return next;
    });

    const handleSave = async () => {
        if (!isDirty || validationError || isSaving) return;

        setIsSaving(true);
        try {
            const payload = { ...settings, burstThreshold: Number(settings.burstThreshold) };
            const res = await api.put('/notifications/settings', payload);
            applyResponse(res.data);
            toast.success('Bildirim ayarları kaydedildi. n8n en geç 1 dakika içinde uygular.');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ayarlar kaydedilemedi.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isAdmin) {
        return <Navigate to="/dashboard" />;
    }

    return (
        <Layout>
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-xl font-bold tracking-tight text-dark-text md:text-3xl">Bildirimler</h1>
                    <p className="text-sm text-dark-muted">WhatsApp bildirimlerinin ne zaman ve nasıl gideceğini yönetin. Ayarlar n8n tarafından her dakika okunur.</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={!isDirty || Boolean(validationError) || isSaving}
                    className="flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
            </div>

            {isLoading && (
                <div className="flex items-center justify-center gap-2 rounded-2xl border border-dark-border bg-dark-card p-10 text-dark-muted">
                    <Loader2 size={20} className="animate-spin" /> Yükleniyor...
                </div>
            )}

            {!isLoading && !settings && (
                <div className="tone-danger flex items-center gap-2 rounded-xl border px-4 py-3 text-sm">
                    <AlertTriangle size={16} /> Ayarlar yüklenemedi. Sayfayı yenileyin.
                </div>
            )}

            {settings && (
                <div className="space-y-5">
                    {validationError && (
                        <div className="tone-warning flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm">
                            <AlertTriangle size={16} /> {validationError}
                        </div>
                    )}

                    <SectionCard icon={<Bell size={20} />} title="Bildirim türleri" description="Kapattığınız türün mesajı gönderilmez; bu sırada oluşan kayıtlar sonradan toplu gönderilmez.">
                        {EVENT_GROUPS.map((group) => (
                            <div key={group.title} className="mb-4 last:mb-0">
                                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-dark-muted">{group.title}</p>
                                {group.items.map((item) => (
                                    <Row key={item.key} label={item.label} hint={item.hint}>
                                        <ToggleSwitch
                                            checked={settings.events[item.key]}
                                            onChange={(value) => update((next) => { next.events[item.key] = value; })}
                                            label={item.label}
                                        />
                                    </Row>
                                ))}
                            </div>
                        ))}
                    </SectionCard>

                    <SectionCard icon={<Moon size={20} />} title="Sessiz saatler" description="Bu saatler arasında mesaj gönderilmez. Biriken bildirimler sessiz saat bitince gider (çok birikirse tek mesajda).">
                        <Row label="Sessiz saatleri kullan" hint="Kapalıysa mesajlar 7/24 gider">
                            <ToggleSwitch
                                checked={settings.quietHours.enabled}
                                onChange={(value) => update((next) => { next.quietHours.enabled = value; })}
                                label="Sessiz saatleri kullan"
                            />
                        </Row>
                        <Row label="Başlangıç" hint="Sessiz dönem bu saatte başlar">
                            <input
                                type="time"
                                value={settings.quietHours.start}
                                disabled={!settings.quietHours.enabled}
                                onChange={(e) => update((next) => { next.quietHours.start = e.target.value; })}
                                className={inputClass}
                                aria-label="Sessiz saat başlangıcı"
                            />
                        </Row>
                        <Row label="Bitiş" hint="Mesajlar bu saatten sonra gönderilir">
                            <input
                                type="time"
                                value={settings.quietHours.end}
                                disabled={!settings.quietHours.enabled}
                                onChange={(e) => update((next) => { next.quietHours.end = e.target.value; })}
                                className={inputClass}
                                aria-label="Sessiz saat bitişi"
                            />
                        </Row>
                    </SectionCard>

                    <SectionCard icon={<Layers size={20} />} title="Toplu mesaj" description="Aynı anda çok sayıda kayıt eklendiğinde gruplar mesajla dolmasın.">
                        <Row label="Eşik" hint="Bir turda bu sayıdan FAZLA bildirim birikirse tek mesajda özetlenir">
                            <input
                                type="number"
                                min="1"
                                max="20"
                                value={settings.burstThreshold}
                                onChange={(e) => update((next) => { next.burstThreshold = e.target.value === '' ? '' : Number(e.target.value); })}
                                className={`${inputClass} w-24 text-right`}
                                aria-label="Toplu mesaj eşiği"
                            />
                        </Row>
                    </SectionCard>

                    <SectionCard icon={<Sun size={20} />} title="Sabah özeti" description="Her gün bir kez; her grup yalnızca kendi konusunun özetini alır, söylenecek bir şey yoksa mesaj gönderilmez.">
                        <Row label="Sabah özetini gönder">
                            <ToggleSwitch
                                checked={settings.morningSummary.enabled}
                                onChange={(value) => update((next) => { next.morningSummary.enabled = value; })}
                                label="Sabah özetini gönder"
                            />
                        </Row>
                        <Row label="Saat" hint="Sessiz saatlere denk gelirse sessiz saat bitince gönderilir">
                            <input
                                type="time"
                                value={settings.morningSummary.time}
                                disabled={!settings.morningSummary.enabled}
                                onChange={(e) => update((next) => { next.morningSummary.time = e.target.value; })}
                                className={inputClass}
                                aria-label="Sabah özeti saati"
                            />
                        </Row>
                        {SUMMARY_SECTIONS.map((section) => (
                            <Row key={section.key} label={section.label} hint={section.hint}>
                                <ToggleSwitch
                                    checked={settings.morningSummary.sections[section.key]}
                                    disabled={!settings.morningSummary.enabled}
                                    onChange={(value) => update((next) => { next.morningSummary.sections[section.key] = value; })}
                                    label={`Özet bölümü: ${section.label}`}
                                />
                            </Row>
                        ))}
                    </SectionCard>

                    <p className="text-xs text-dark-muted">
                        {meta.isDefault
                            ? 'Henüz kaydedilmiş bir ayar yok; varsayılan değerler kullanılıyor.'
                            : `Son değişiklik: ${meta.updatedBy || '—'} • ${formatRecordDate(meta.updatedAt)} ${formatRecordTime(meta.updatedAt)}`}
                    </p>
                </div>
            )}
        </Layout>
    );
};

export default NotificationSettings;
